// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// <GetUserSnippet>
import { convertUnicode } from '@/utilities/contents'
import {
    Client,
    GraphRequestOptions,
    PageCollection,
    PageIterator,
} from '@microsoft/microsoft-graph-client'
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser'
import { endOfWeek, startOfWeek, startOfDay, endOfDay } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { User, Event, TodoTask, TodoTaskList } from 'microsoft-graph'

let graphClient: Client | undefined = undefined

function ensureClient(authProvider: AuthCodeMSALBrowserAuthenticationProvider) {
    if (!graphClient) {
        graphClient = Client.initWithMiddleware({
            authProvider: authProvider,
        })
    }

    return graphClient
}

export async function getUser(
    authProvider: AuthCodeMSALBrowserAuthenticationProvider,
): Promise<User> {
    const graphClient = ensureClient(authProvider)

    // Return the /me API endpoint result as a User object
    const user: User = await graphClient
        .api('/me')
        // Only retrieve the specific fields needed
        .select('displayName,mail,userPrincipalName')
        .get()

    return user
}
// </GetUserSnippet>

export async function getUserTaskLists(
    authProvider: AuthCodeMSALBrowserAuthenticationProvider,
): Promise<TodoTaskList[]> {
    try {
        const graphClient = ensureClient(authProvider)

        // Return the /me API endpoint result as a User object
        const todoTaskList: any = await graphClient.api('/me/todo/lists').get()

        return todoTaskList.value
    } catch (e: any) {
        console.log(e.message)
        return [] as TodoTaskList[]
    }
}
// </GetUserTaskLists>

export async function getUserListTasks(
    authProvider: AuthCodeMSALBrowserAuthenticationProvider,
    listId: string,
): Promise<TodoTask[]> {
    try {
        const graphClient = ensureClient(authProvider)

        // Return the /me API endpoint result as a User object
        const listTasks: any = await graphClient
            .api(`/me/todo/lists/${listId}/tasks`)
            .get()

        return listTasks.value
    } catch (e: any) {
        console.log(e.message)
        return [] as TodoTask[]
    }
}
// </GetUserTaskLists>

// <GetUserWeekCalendarSnippet>
export async function getUserWeekCalendar(
    authProvider: AuthCodeMSALBrowserAuthenticationProvider,
    timeZone: string,
    date?: string,
): Promise<Event[]> {
    const graphClient = ensureClient(authProvider)

    // Generate startDateTime and endDateTime query params
    let startDateTime
    let endDateTime
    if (date) {
        const now = new Date(date)
        startDateTime = zonedTimeToUtc(startOfDay(now), timeZone).toISOString()
        endDateTime = zonedTimeToUtc(endOfDay(now), timeZone).toISOString()
    } else {
        // to display a 7-day window
        const now = new Date()
        startDateTime = zonedTimeToUtc(startOfWeek(now), timeZone).toISOString()
        endDateTime = zonedTimeToUtc(endOfWeek(now), timeZone).toISOString()
    }

    // console.log('Start Time: ', startDateTime, 'EndTime :', endDateTime)

    // GET /me/calendarview?startDateTime=''&endDateTime=''
    // &$select=subject,organizer,start,end
    // &$orderby=start/dateTime
    // &$top=50
    const response: PageCollection = await graphClient
        .api('/me/calendarview')
        .header(
            'Prefer',
            `outlook.timezone="${timeZone}",outlook.body-content-type="html"`,
        )
        .query({ startDateTime: startDateTime, endDateTime: endDateTime })
        .select('subject,organizer,start,end,body,webLink,onlineMeeting,location')
        .orderby('start/dateTime')
        .top(25)
        .get()

    if (response['@odata.nextLink']) {
        // Presence of the nextLink property indicates more results are available
        // Use a page iterator to get all results
        const events: Event[] = []

        // Must include the time zone header in page
        // requests too
        const options: GraphRequestOptions = {
            headers: { Prefer: `outlook.timezone="${timeZone}"` },
        }

        const pageIterator = new PageIterator(
            graphClient,
            response,
            event => {
                events.push(event)
                return true
            },
            options,
        )

        await pageIterator.iterate()

        return events
    } else {
        return response.value
    }
}
// </GetUserWeekCalendarSnippet>

// <CreateEventSnippet>
export async function createEvent(
    authProvider: AuthCodeMSALBrowserAuthenticationProvider,
    newEvent: Event,
): Promise<Event> {
    const graphClient = ensureClient(authProvider)

    // POST /me/events
    // JSON representation of the new event is sent in the
    // request body
    return await graphClient.api('/me/events').post(newEvent)
}
// </CreateEventSnippet>

// <DeleteEventSnippet>
export async function deleteEvent(
    authProvider: AuthCodeMSALBrowserAuthenticationProvider,
    eventId: string,
): Promise<Event> {
    const graphClient = ensureClient(authProvider)

    // DELETE /me/events/{id}
    // Delete Event based on ID
    return await graphClient.api(`/me/events/${eventId}`).delete()
}
// </DeleteEventSnippet>

// <GetUserMailFolders>
export async function getUserMailFolders(
    accessToken: string,
): Promise<Event[]> {
    const data = await fetch(
        'https://graph.microsoft.com/beta/me/mailfolders',
        {
            method: 'GET',
            headers: {
                Authorization: accessToken,
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
        },
    )
    const response = await data.json()
    return response.value
}
// </GetUserMailFolders>
export type MailFolderReturnType = {
    messages: Event[]
    unreadCount: number
    totalCount: number
}

// <GetUserMail>
export async function getUserMail(
    accessToken: string,
    authProvider: AuthCodeMSALBrowserAuthenticationProvider,
    timeZone: string,
    folder: string,
): Promise<MailFolderReturnType> {
    // https://graph.microsoft.com/v1.0/me/messages?
    // $filter=isRead eq false
    // &$select=bodyPreview,isRead,createdDateTime,sentDateTime,subject,webLink
    // &$top=25
    // &$orderby=sentDateTime desc

    // https://graph.microsoft.com/v1.0/me/messages?
    // $select=bodyPreview,isRead,createdDateTime,sentDateTime,subject,webLink
    // &$top=10
    // &$orderby=sentDateTime desc

    const folders: any = await getUserMailFolders(accessToken)
    // console.log(folders)
    if (folders && folders.length) {
        const index = folders.findIndex(
            (item: any) =>
                item.wellKnownName &&
                item.wellKnownName.toLowerCase() === folder.toLowerCase(),
        )
        if (index > -1) {
            const folder = folders[index]
            const folderID = folder.id
            const response: Event[] = await getUserMailByFolder(
                authProvider,
                timeZone,
                folderID,
            )
            return {
                messages: response,
                unreadCount: folder.unreadItemCount,
                totalCount: folder.totalItemCount,
            }
        } else {
            return { messages: [], unreadCount: 0, totalCount: 0 }
        }
    } else {
        return { messages: [], unreadCount: 0, totalCount: 0 }
    }
}
// </GetUserMail>

// <GetUserMailByFolder>
export async function getUserMailByFolder(
    authProvider: AuthCodeMSALBrowserAuthenticationProvider,
    timeZone: string,
    mailFolderId: string,
): Promise<Event[]> {
    const graphClient = ensureClient(authProvider)

    // https://graph.microsoft.com/v1.0/me/messages?
    // $filter=isRead eq false
    // &$select=bodyPreview,isRead,createdDateTime,sentDateTime,subject,webLink
    // &$top=25
    // &$orderby=sentDateTime desc

    // https://graph.microsoft.com/v1.0/me/messages?
    // $select=bodyPreview,isRead,createdDateTime,sentDateTime,subject,webLink
    // &$top=10
    // &$orderby=sentDateTime desc

    const response: PageCollection = await graphClient
        .api(`/me/mailFolders/${mailFolderId}/messages`)
        .header('Prefer', `outlook.timezone="${timeZone}",outlook.body-content-type="html"`)
        .select(
            'bodyPreview,isRead,createdDateTime,sentDateTime,subject,webLink,sender,body',
        )
        .orderby('sentDateTime desc')
        .top(10)
        .get()

    return response.value
}
// </GetUserMailByFolder>

// <getUserDetailsByQuery>
export async function getUserDetailsByQuery(
    authProvider: AuthCodeMSALBrowserAuthenticationProvider,
    query: string,
    timeZone: string,
): Promise<Event[]> {
    try {
        const graphClient = ensureClient(authProvider)
        query = query.replace(/,/g, '+').replace(/#/g, '+')
        const response: PageCollection = await graphClient
            .api(`me/people`)
            //.header('Prefer', `outlook.timezone="${timeZone}"`)
            .search(convertUnicode(`"${query}"`))
            .filter(
                `personType/subclass eq 'OrganizationUser' and givenName ne null`,
            )
            .top(10)
            .get()

        return response.value
    } catch (err) {
        console.log('GRAPH API ERR :', err)
        return []
    }
}
// </getUserDetailsByQuery>

// <getUserDetailsByQuery>
export async function getUserDetails(
    authProvider: AuthCodeMSALBrowserAuthenticationProvider,
    query: string,
    select: string[],
): Promise<Event[]> {
    try {
        const graphClient = ensureClient(authProvider)
        const selector: string = select.join(',')
        query = query.replace(/,/g, '+').replace(/#/g, '+')
        const response: PageCollection = await graphClient
            .api(`me/people`)
            .select(selector)
            .search(`"${query}"`)
            .filter(
                `personType/subclass eq 'OrganizationUser' and givenName ne null`,
            )
            .top(10)
            .get()

        return response.value
    } catch (err) {
        console.log('GRAPH API ERR :', err)
        return []
    }
}
// </getUserDetailsByQuery>

// <getDrives>
export async function customizedAPI(
    authProvider: AuthCodeMSALBrowserAuthenticationProvider,
    query: string,
    timeZone?: string,
): Promise<any> {
    try {
        const graphClient = ensureClient(authProvider)
        const response: PageCollection = await graphClient.api(query).get()
        return response
    } catch (err) {
        console.log('GRAPH API ERR :', err)
        return null
    }
}
// </getUserDetailsByQuery>

export async function FindMeetingTime(
    authProvider: AuthCodeMSALBrowserAuthenticationProvider,
    newEvent: any,
    timeZone: string
): Promise<any>{
    const prefer= `outlook.timezone="Pacific Standard Time"`
    const graphClient = ensureClient(authProvider)

    // POST /me/events
    // JSON representation of the new event is sent in the
    // request body
    return await graphClient.api('/me/findMeetingTimes').header(
        'Prefer',
            `outlook.timezone="${timeZone}"`,
    ).post(newEvent)
}

export async function getUsed(
    authProvider: AuthCodeMSALBrowserAuthenticationProvider 
    ): Promise<any> {
        const client = ensureClient(authProvider);

        return await client.api('/me/insights/used')
        .orderby('LastUsed/LastAccessedDateTime desc')
        .filter("ResourceReference/Type eq 'microsoft.graph.driveItem' ")
        .get();
    
}

export async function oneDriveUsed(
    authProvider: AuthCodeMSALBrowserAuthenticationProvider 
    ): Promise<any> {
        const client = ensureClient(authProvider);
        return await client.api('/me/drive/root/children')
        .get();

}
export async function oneDriveChildren(
    authProvider: AuthCodeMSALBrowserAuthenticationProvider ,
    itemId:string
    ): Promise<any> {
        const clients = ensureClient(authProvider);
        return await clients.api(`/me/drive/items/${itemId}/children`)
        .get();
    }


    export async function getPlannerList(
        accessToken: string,
    ): Promise<any> {
        const data = await fetch(
            'https://graph.microsoft.com/beta/me/planner',
            {
                method: 'GET',
                headers: {
                    Authorization: accessToken,
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            },
        )
        const response = await data.json()
        return response
    }
